// Global flash messages (requirement 9.2).
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import type { FlashbarProps } from '@cloudscape-design/components/flashbar';

type Item = FlashbarProps.MessageDefinition;
interface Notify {
  items: Item[];
  success: (content: string) => void;
  error: (content: string) => void;
  info: (content: string) => void;
}

const Ctx = createContext<Notify | null>(null);
let seq = 0;

export function NotifyProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const push = useCallback((type: Item['type'], content: string) => {
    const id = `n${++seq}`;
    const dismiss = () => setItems((xs) => xs.filter((x) => x.id !== id));
    setItems((xs) => [
      ...xs,
      { id, type, content, dismissible: true, onDismiss: dismiss, dismissLabel: 'Dismiss message' },
    ]);
    if (type === 'success' || type === 'info') window.setTimeout(dismiss, 6000);
  }, []);
  return (
    <Ctx.Provider
      value={{
        items,
        success: (c) => push('success', c),
        error: (c) => push('error', c),
        info: (c) => push('info', c),
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useNotify(): Notify {
  const c = useContext(Ctx);
  if (!c) throw new Error('useNotify outside NotifyProvider');
  return c;
}

export const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : 'Something went wrong.');
